import React, { useEffect } from 'react';
import { Grid, Alert } from '@mui/material';
import {
    Select,
    DateInput,
    Input,
    WeightInput,
    SearchableDropdown, 
    BinaryButtonGroup,
    InformationAlert
} from '../../Common';
import { testResultsOptions } from '../utils';
import { isMobile } from '../../../utils';
import LeakLocationAutocomplete from './LeakLocationAutocomplete';
import Enums from '../../../enums';
import { TranslateW } from "../../../Utils/Languages/Translate"
import { lightRed, red } from '../../../color_utils';


const FollowUpVerificationTest = ({
    formState,
    setGlobalState,
    dropdownOptions,
    requiredFieldsMissing:{
        verificationLeakInspection: verificationLeakInspectionErrors = {}
    },
    // setRequiredFields,
    fieldsWithErrors: {
        inspectionDate: {
            errorExists = false,
            helperText = "",
        } = {}
    },
    requiredFieldsRef,
}) => {
    const underRepairStatusIds = [1, 2];
    const noLeakRepairOrRepairAttempted = underRepairStatusIds.includes(formState.leakRepairDispositionTypeId);

    const setBasicRequiredFields = () => {
        requiredFieldsRef.current = {
            ...requiredFieldsRef.current,
            "FollowUpVerificationService": [
                "inspectionDate",
                "leakDetectionMethodId",
                "wasLeakFound",
            ]
        };
    };

    // Leak Repair Status is the label name for the input compoment
    // maps to backend field leakRepairDispositionTypeId
    const setLeakRepairStatusRequired = () => {
        requiredFieldsRef.current = {
            ...requiredFieldsRef.current,
            "FollowUpVerificationService": [
                ...requiredFieldsRef.current["FollowUpVerificationService"],
                "leakRepairDispositionTypeId",
            ]
        };
    };

    const deleteFollowUpLeakInspectionValues = () => {
        delete formState.verificationLeakInspection.leakLocationCategoryId;
        delete formState.verificationLeakInspection.leakLocationId;
        delete formState.verificationLeakInspection.faultCauseTypeId;
        delete formState.verificationLeakInspection.estimatedLeakAmount;
        setBasicRequiredFields();

        if (
            formState.initialLeakInspection.wasLeakFound === true
            && formState.initialVerificationLeakInspection.wasLeakFound === false
            && !requiredFieldsRef.current["FollowUpVerificationService"].includes("leakRepairDispositionTypeId")
        ) {
            setLeakRepairStatusRequired();
        }
    };

    const handleLeakFoundCircuitOpened = (leakFound, isNested) => {
        if (leakFound === true) {
            requiredFieldsRef.current = {
                ...requiredFieldsRef.current,
                "FollowUpVerificationService": [
                    "inspectionDate",
                    "leakDetectionMethodId",
                    "wasLeakFound",
                    "leakLocationId",
                    "faultCauseTypeId",
                ]
            };
        }

        if (leakFound === false) {
            deleteFollowUpLeakInspectionValues();
        }

        if (
            formState.initialLeakInspection.wasLeakFound === true
            && formState.initialVerificationLeakInspection.wasLeakFound === false
            && !requiredFieldsRef.current["FollowUpVerificationService"].includes("leakRepairDispositionTypeId")
        ) {
            setLeakRepairStatusRequired();
        }

        return setGlobalState("verificationLeakInspection.wasLeakFound", leakFound, isNested);
    };

    const handleLeakRepairStatus = (newLeakRepairStatus) => {
        setGlobalState("verificationLeakInspection.leakRepairDispositionTypeId", newLeakRepairStatus, true);
        if (underRepairStatusIds.includes(newLeakRepairStatus)) {
            setGlobalState("finalCoolingApplianceStatusId", Enums.SE.CoolingApplianceStatus.UnderRepair);
        }
    };

    const handleLeakLocation = ({ leakLocationId, leakLocationCategoryId }) => {
        setGlobalState("verificationLeakInspection.leakLocationId", leakLocationId, true);
        setGlobalState("verificationLeakInspection.leakLocationCategoryId", leakLocationCategoryId, true);
    };

    useEffect(() => {
        handleLeakFoundCircuitOpened(formState.verificationLeakInspection.wasLeakFound, true);
    }, []);

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <DateInput
                    showTodayButton
                    allowPast
                    allowFuture={false}
                    label={`${TranslateW("leakRepair.date-followup-service")}*`}
                    date={formState.verificationLeakInspection.inspectionDate}
                    error={verificationLeakInspectionErrors.inspectionDate || errorExists}
                    customHelperText={helperText}
                    onChange={new_date => setGlobalState("verificationLeakInspection.inspectionDate", new_date, true)}
                />
            </Grid>
            {formState.initialLeakInspection.wasLeakFound ? (
                <Grid item xs={12} md={6}>
                    <Select
                        required
                        label={TranslateW("showLeakRepairStatus.leak-repair-status")}
                        value={formState.verificationLeakInspection.leakRepairDispositionTypeId}
                        onChange={e => handleLeakRepairStatus(e.target.value)}
                        options={dropdownOptions.repairStatuses}
                        error={verificationLeakInspectionErrors.leakRepairDispositionTypeId}
                    />
                </Grid>
            ) : (
                <Grid item xs={12} md={6} />
            )}
            <Grid item xs={12} md={6}>
                <Select
                    required
                    label={TranslateW("leakInspectionView.leak-detection-method")}
                    value={formState.verificationLeakInspection.leakDetectionMethodId}
                    onChange={e => setGlobalState("verificationLeakInspection.leakDetectionMethodId", e.target.value, true)}
                    options={dropdownOptions.leakDetectionMethods}
                    error={verificationLeakInspectionErrors.leakDetectionMethodId}
                />
            </Grid>
            <Grid item xs={12}>
                <BinaryButtonGroup
                    id="verificationLeakInspectionWasLeakFound"
                    required
                    label={TranslateW("leakRepair.verification-results")}
                    value={formState.verificationLeakInspection.wasLeakFound}
                    onChange={selectedValue => handleLeakFoundCircuitOpened(selectedValue, true)}
                    options={testResultsOptions.map(option => ({
                        value: option.id,
                        label: option.name,
                        ...(option.id === true && {
                            selectedColor: red,
                            selectedBackgroundColor: lightRed,
                        }),
                    }))}
                    error={verificationLeakInspectionErrors.wasLeakFound}
                />
            </Grid>
            {formState.verificationLeakInspection.wasLeakFound === true && (
                <>
                    <Grid item xs={12} md={6}>
                        <LeakLocationAutocomplete
                            required
                            label={TranslateW("leakLocationAndCause.leak-location")}
                            leakLocations={dropdownOptions.leakLocations}
                            leakLocationCategories={dropdownOptions.leakLocationCategories}
                            leakLocationId={formState.verificationLeakInspection.leakLocationId}
                            onChange={handleLeakLocation}
                            error={verificationLeakInspectionErrors.leakLocationId}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SearchableDropdown
                            required
                            label={TranslateW("leakLocationAndCause.cause-of-leak-for-asset")}
                            value={formState.verificationLeakInspection.faultCauseTypeId}
                            onChange={id => setGlobalState("verificationLeakInspection.faultCauseTypeId", id, true)}
                            error={verificationLeakInspectionErrors.faultCauseTypeId}
                            options={dropdownOptions.leakCauses}
                            searchFields={['name']}
                        />
                    </Grid>
                    <Grid container spacing={2} item xs={12} md={6}>
                        <WeightInput
                            removePadding
                            showConversionArrows
                            columnSpan={5}
                            header={TranslateW("EstimatedLeakAmount") }
                            variant="standard"
                            value={formState.verificationLeakInspection.estimatedLeakAmount}
                            onChange={new_weight => setGlobalState("verificationLeakInspection.estimatedLeakAmount", new_weight, true)}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12} md={6}>
                <Input
                    fullWidth
                    multiLine
                    label={TranslateW("Notes") }
                    value={formState.verificationLeakInspection.notes}
                    onChange={e => setGlobalState("verificationLeakInspection.notes", e.target.value, true)}
                />
                {formState.verificationLeakInspection.leakTestPassFail === 1 && (
                    <Alert severity="warning">
                        {TranslateW("ActualGasSection") }
                    </Alert>
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                <Select
                    label={TranslateW("generalService.status-after-service") }
                    value={formState.finalCoolingApplianceStatusId}
                    onChange={e => setGlobalState("finalCoolingApplianceStatusId", e.target.value)}
                    options={dropdownOptions.afterServiceStatusOptions}
                    disabled={
                        formState.finalCoolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair
                        && noLeakRepairOrRepairAttempted
                    }
                />
            </Grid>
            {formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.LeakNotRepaired
                && formState.initialVerificationLeakInspection.wasLeakFound === false
                && formState.verificationLeakInspection.wasLeakFound === true && (
                <Grid item xs={12} md={12} lg={12}>
                    <Alert severity="info">
                        {TranslateW("repairOutcome.SubGuidance.FvtFail")}
                    </Alert>
                </Grid>
            )}
        </Grid>
    );
};

export default FollowUpVerificationTest;
